import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { label: "Work", href: "#work" },
  { label: "About", href: "#about" },
  { label: "CV", href: "#cv" },
];

/**
 * Fixed navigation bar with blur backdrop.
 * Adds a border on scroll to signal depth.
 */
export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      setHidden(y > lastScrollY.current && y > 80);
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 flex justify-center pt-4 px-4 transition-transform duration-300",
      hidden ? "-translate-y-full" : "translate-y-0"
    )}>
      <nav
        className={cn(
          "w-full max-w-2xl flex items-center justify-between px-5 h-12 rounded-full transition-all duration-300",
          "bg-white/70 backdrop-blur-xl border border-white/60",
          scrolled ? "shadow-lg shadow-black/5" : "shadow-md shadow-black/[0.03]"
        )}
      >
        {/* Logo / Name */}
        <a
          href="#"
          className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] text-sm tracking-tight hover:text-[#1D3FE8] transition-colors"
        >
          Verónica Aguilar
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors font-[DM_Sans]"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            className="px-4 py-1.5 rounded-full bg-[#1D3FE8] text-white text-sm font-medium font-[DM_Sans] hover:bg-[#1532c0] transition-colors"
          >
            Let's talk
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={cn("block w-5 h-0.5 bg-[#1A1A1A] transition-all", menuOpen && "rotate-45 translate-y-2")} />
          <span className={cn("block w-5 h-0.5 bg-[#1A1A1A] transition-all", menuOpen && "opacity-0")} />
          <span className={cn("block w-5 h-0.5 bg-[#1A1A1A] transition-all", menuOpen && "-rotate-45 -translate-y-2")} />
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden absolute top-full mt-2 left-4 right-4 bg-white/90 backdrop-blur-xl border border-white/60 rounded-2xl shadow-lg px-6 py-4 flex flex-col gap-4">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-sm font-medium text-[#1A1A1A] font-[DM_Sans]"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setMenuOpen(false)}
            className="w-fit px-4 py-2 rounded-full bg-[#1D3FE8] text-white text-sm font-medium font-[DM_Sans]"
          >
            Let's talk
          </a>
        </div>
      )}
    </header>
  );
}
